import api from './api.ts';

const TOKEN_KEY = 'tbkmeet_token';
const REFRESH_TOKEN_KEY = 'tbkmeet_refresh_token';
const USER_KEY = 'tbkmeet_user';

// OAuth (sosyal medya kayit/giris) icin sessionStorage anahtarlari.
// State CSRF koruma: provider'a yonlendirmeden once kaydedilir,
// callback geri donduginde URL'deki state ile karsilastirilir.
const OAUTH_STATE_KEY = 'tbkmeet_oauth_state';
const OAUTH_PROVIDER_KEY = 'tbkmeet_oauth_provider';
const OAUTH_REDIRECT_URI_KEY = 'tbkmeet_oauth_redirect_uri';

export type OAuthProvider = 'github' | 'google' | 'linkedin';
export interface ProfilePayload {
  name: string;
  phone: string;
  bio: string;
  language: string;
  timezone: string;
  dateFormat: string;
  timeFormat: string;
}

// İstemci-tarafı basit throttle. Sunucudaki rate-limit'in yerine değildir;
// sadece çok hızlı tıklama ve botların gereksiz istek yağdırmasını engeller.
// Asıl koruma sunucu (Nginx limit_req + backend) tarafında olmalıdır.
const lastCallAt: Record<string, number> = {};
const THROTTLE_MS = {
  login: 1000,
  register: 1000,
  forgotPassword: 3000,
  resetPassword: 1500,
  oauthStart: 1500,
  resendVerification: 3000,
};

function generateOAuthState(): string {
  const arr = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(arr);
  } else {
    for (let i = 0; i < arr.length; i++) arr[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('');
}

function clientThrottle(key: keyof typeof THROTTLE_MS) {
  const now = Date.now();
  const last = lastCallAt[key] || 0;
  const interval = THROTTLE_MS[key];
  if (now - last < interval) {
    const wait = Math.ceil((interval - (now - last)) / 1000);
    throw new Error(
      `Çok hızlı istek gönderiliyor. Lütfen ${wait} sn bekleyip tekrar deneyin.`
    );
  }
  lastCallAt[key] = now;
}

export const AUTH_CHANGED_EVENT = 'tbkmeet-auth-changed';

function notifyAuthChanged() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
  }
}

function normalizeUser(user: any) {
  if (!user || typeof user !== 'object') return user;
  const emailVerified =
    user.emailVerified === true ||
    user.email_verified === true;
  return {
    ...user,
    emailVerified,
    email_verified: emailVerified,
  };
}

function setStoredUser(user: any) {
  localStorage.setItem(USER_KEY, JSON.stringify(normalizeUser(user)));
  notifyAuthChanged();
}

function setStoredAuth(token: string, refreshToken: string | undefined, user: any) {
  localStorage.setItem(TOKEN_KEY, token);
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
  setStoredUser(user);
}

export const authService = {
  async login(email: string, password: string) {
    clientThrottle('login');
    try {
      const response = await api.post('/api/auth/login', { email, password });
      if (response.data.success) {
        const { token, refreshToken, user } = response.data.data;
        setStoredAuth(token, refreshToken, user);
        return { success: true, user };
      }
      throw new Error(response.data.error || 'Giriş başarısız');
    } catch (err: any) {
      const serverError = err?.response?.data?.error || err?.response?.data?.message;
      const raw = typeof serverError === 'string' ? serverError : err?.message || '';
      if (/refresh token|No Refresh|Invalid email or password|Unauthorized|401/i.test(raw)) {
        throw new Error('E-posta veya şifre hatalı');
      }
      throw new Error(raw || 'Giriş başarısız');
    }
  },

  async register(name: string, email: string, password: string) {
    clientThrottle('register');
    try {
      const response = await api.post('/api/auth/register', { name, email, password });
      if (response.data.success) {
        const { token, refreshToken, user } = response.data.data;
        setStoredAuth(token, refreshToken, user);
        return { success: true, user };
      }
      throw new Error(response.data.error || 'Kayıt başarısız');
    } catch (err: any) {
      const serverError = err?.response?.data?.error || err?.response?.data?.message;
      if (typeof serverError === 'string') throw new Error(serverError);
      if (Array.isArray(serverError)) {
        throw new Error(serverError.map((e: any) => e.message || e).join(' '));
      }
      throw new Error(err?.message || 'Kayıt başarısız');
    }
  },

  getToken() {
    return localStorage.getItem(TOKEN_KEY);
  },

  getRefreshToken() {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  getUser() {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try {
      return normalizeUser(JSON.parse(raw));
    } catch {
      return null;
    }
  },

  isAuthenticated() {
    return !!this.getToken();
  },

  logout() {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (refreshToken) {
      api.post('/api/auth/logout', { refreshToken }).catch(() => {});
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    notifyAuthChanged();
  },

  async fetchProfile() {
    const response = await api.get('/api/auth/me');
    if (!response.data?.success) {
      throw new Error(response.data?.error || 'Profil bilgileri alınamadı');
    }
    const user = response.data.data;
    setStoredUser(user);
    return user;
  },

  async updateProfile(payload: ProfilePayload) {
    const response = await api.patch('/api/auth/me', payload);
    if (!response.data?.success) {
      throw new Error(response.data?.error || 'Profil güncellenemedi');
    }
    const user = response.data.data;
    setStoredUser(user);
    return user;
  },

  async deleteAccount(confirmEmail: string) {
    try {
      const response = await api.post('/api/auth/delete-account', { confirmEmail });
      if (!response.data?.success) {
        const err = response.data?.error;
        throw new Error(
          typeof err === 'string' ? err : 'Hesap silinemedi'
        );
      }

      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      notifyAuthChanged();
      return response.data.data;
    } catch (err: any) {
      const serverError = err?.response?.data?.error;
      if (typeof serverError === 'string') {
        throw new Error(serverError);
      }
      throw new Error(err?.message || 'Hesap silinemedi');
    }
  },

  async verifyEmail(token: string) {
    try {
      const response = await api.post('/api/auth/verify-email', { token });
      if (!response.data?.success) {
        throw new Error(response.data?.error || 'E-posta doğrulanamadı');
      }
      const user = {
        ...(response.data.data || {}),
        emailVerified: true,
      };
      // Token yoksa (mail başka cihazda açıldı) sadece user yazılır; girişli sekme fetchProfile ile yakalar.
      setStoredUser(user);
      return user;
    } catch (err: any) {
      const serverError = err?.response?.data?.error;
      if (typeof serverError === 'string') {
        throw new Error(serverError);
      }
      throw new Error(err?.message || 'E-posta doğrulanamadı');
    }
  },

  async resendVerification() {
    clientThrottle('resendVerification');
    try {
      const response = await api.post('/api/auth/resend-verification');
      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Doğrulama maili gönderilemedi');
      }
      return { success: true };
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) {
        throw new Error(
          'Doğrulama maili servisi şu an kullanılamıyor (sunucu güncellemesi gerekli).'
        );
      }
      const serverError = err?.response?.data?.error;
      if (typeof serverError === 'string') {
        throw new Error(serverError);
      }
      throw new Error(err?.message || 'Doğrulama maili gönderilemedi');
    }
  },

  async forgotPassword(email: string) {
    // E-posta numaralandırma saldırılarını önlemek için backend genelde
    // hesabın varlığından bağımsız olarak başarılı yanıt döner.
    clientThrottle('forgotPassword');
    const response = await api.post('/api/auth/forgot-password', { email });
    if (response.data?.success === false) {
      throw new Error(response.data.error || 'İstek gönderilemedi');
    }
    return { success: true };
  },

  async resetPassword(token: string, password: string) {
    clientThrottle('resetPassword');
    const response = await api.post('/api/auth/reset-password', { token, password });
    if (response.data?.success === false) {
      throw new Error(response.data.error || 'Şifre sıfırlanamadı');
    }
    return { success: true };
  },

  // ===========================================================================
  // OAuth — sosyal medya kayit/giris
  // ===========================================================================
  // startOAuth: backend'den authorize URL config'ini alir, state olusturur,
  //             sessionStorage'a kaydeder, provider'a yonlendirir.
  // completeOAuth: callback sayfasinda cagrilir. URL'den code+state alir,
  //                sessionStorage ile dogrular, backend'e POST eder, JWT alir.
  // ---------------------------------------------------------------------------
  async startOAuth(provider: OAuthProvider): Promise<void> {
    clientThrottle('oauthStart');

    const cfgRes = await api.get(`/api/auth/oauth/${provider}/config`);
    if (!cfgRes.data?.success || !cfgRes.data?.data?.authorizeUrl) {
      throw new Error(cfgRes.data?.error || 'OAuth yapilandirmasi alinamadi');
    }

    const { authorizeUrl, clientId, scope } = cfgRes.data.data;
    const state = generateOAuthState();
    const redirectUri = `${window.location.origin}/oauth/callback`;

    sessionStorage.setItem(OAUTH_STATE_KEY, state);
    sessionStorage.setItem(OAUTH_PROVIDER_KEY, provider);
    sessionStorage.setItem(OAUTH_REDIRECT_URI_KEY, redirectUri);

    const url = new URL(authorizeUrl);
    url.searchParams.set('client_id', clientId);
    url.searchParams.set('redirect_uri', redirectUri);
    url.searchParams.set('scope', scope);
    url.searchParams.set('state', state);
    url.searchParams.set('response_type', 'code');
    // Google icin best practice: prompt+access_type opsiyonel parametreler
    if (provider === 'google') {
      url.searchParams.set('access_type', 'online');
      url.searchParams.set('prompt', 'consent select_account');
    }

    window.location.assign(url.toString());
  },

  async completeOAuth(code: string, returnedState: string): Promise<{ success: true; user: any }> {
    const savedState = sessionStorage.getItem(OAUTH_STATE_KEY);
    const provider = sessionStorage.getItem(OAUTH_PROVIDER_KEY);
    const redirectUri = sessionStorage.getItem(OAUTH_REDIRECT_URI_KEY);

    if (!savedState || !provider || !redirectUri) {
      throw new Error(
        'OAuth oturumu bulunamadı. Lütfen tekrar giriş yapmayı deneyin.'
      );
    }
    if (savedState !== returnedState) {
      // sessionStorage'i her halukarda temizle
      sessionStorage.removeItem(OAUTH_STATE_KEY);
      sessionStorage.removeItem(OAUTH_PROVIDER_KEY);
      sessionStorage.removeItem(OAUTH_REDIRECT_URI_KEY);
      throw new Error('OAuth state uyuşmuyor (CSRF koruması). Lütfen tekrar deneyin.');
    }

    // Cleanup — re-use'u onle
    sessionStorage.removeItem(OAUTH_STATE_KEY);
    sessionStorage.removeItem(OAUTH_PROVIDER_KEY);
    sessionStorage.removeItem(OAUTH_REDIRECT_URI_KEY);

    const response = await api.post(`/api/auth/oauth/${provider}`, {
      code,
      redirect_uri: redirectUri,
      state: returnedState,
    });

    if (!response.data?.success) {
      const err = response.data?.error;
      throw new Error(
        typeof err === 'string' ? err : 'OAuth girişi başarısız oldu'
      );
    }

    const { token, refreshToken, user } = response.data.data;
    setStoredAuth(token, refreshToken, user);
    return { success: true, user };
  },
};